/**
 * SND-16242: Desktop inline search shell.
 *
 * Owns only the open/close state of the suggestions popover; the actual search
 * (fetch, morph, keyboard navigation) is handled by the theme's
 * predictive-search-component that lives inside this element.
 */
class SearchInline extends HTMLElement {
  static MAX_PRODUCTS = 4;

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const input = this.#input;
    if (!input) return;

    // The theme's predictive-search component calls searchInput.focus() on its own
    // (e.g. after loading "recently viewed" on page load). Only a real user gesture
    // may open the popover: pointer on the field, Tab into it, typing, or a shortcut.
    // A programmatic focus without intent is undone so the field never looks "active".
    let intent = false;
    const armIntent = () => {
      intent = true;
      setTimeout(() => (intent = false), 400);
    };

    input.addEventListener('pointerdown', armIntent, { signal });
    input.addEventListener(
      'focus',
      () => {
        if (this.hasAttribute('data-open')) return;
        if (intent) {
          this.open();
        } else {
          input.blur();
        }
      },
      { signal }
    );
    input.addEventListener('input', () => this.open(), { signal });

    // Click/tap outside closes; clicks inside (results, pills, footer) keep it open
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.hasAttribute('data-open')) return;
        if (event.target instanceof Node && this.contains(event.target)) return;
        this.close();
      },
      { signal }
    );

    // Tabbing out of the component closes it
    this.addEventListener(
      'focusout',
      (event) => {
        const next = event.relatedTarget;
        if (next instanceof Node && this.contains(next)) return;
        if (next === null) return; // window blur / click on non-focusable inside: pointerdown decides
        this.close();
      },
      { signal }
    );

    // Global shortcuts: ⌘K / Ctrl+K, or "/" outside form fields.
    // Capture phase so the modal's own ⌘K listener does not also fire.
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.hasAttribute('data-open')) {
          this.close();
          input.blur();
          return;
        }

        if (event.key === 'Tab' && !this.hasAttribute('data-open')) {
          armIntent(); // Tab landing on the field counts as user intent
          return;
        }

        // Quiet shortcuts (not advertised in the UI): "f" or "/" outside form fields, ⌘K / Ctrl+K.
        const plainKey = !event.metaKey && !event.ctrlKey && !event.altKey;
        const isShortcut =
          ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === 'k') ||
          (plainKey && (event.key === 'f' || event.key === 'F' || event.key === '/') && !this.#isTypingContext());

        if (!isShortcut || !this.#isVisible()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.open();
        input.focus();
        input.select();
      },
      { signal, capture: true }
    );

    // Keep the popover compact: one row of products (the results section returns up to 8).
    // Trimming the DOM (not just hiding) keeps predictive-search keyboard navigation in sync.
    const results = this.querySelector('.search-inline__results');
    if (results) {
      const trim = () => {
        for (const list of results.querySelectorAll('.predictive-search-results__wrapper-products')) {
          const cards = list.querySelectorAll('.predictive-search-results__card--product');
          for (let i = SearchInline.MAX_PRODUCTS; i < cards.length; i++) cards[i].remove();
        }
      };
      trim();
      new MutationObserver(trim).observe(results, { childList: true, subtree: true });
    }

    // Close when the header hides on scroll so the popover never floats alone
    window.addEventListener(
      'scroll',
      () => {
        if (!this.hasAttribute('data-open')) return;
        const rect = this.getBoundingClientRect();
        if (rect.bottom < 0) {
          this.close();
          input.blur();
        }
      },
      { signal, passive: true }
    );
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #input() {
    return this.querySelector('input.search-inline__input');
  }

  get #popover() {
    return this.querySelector('.search-inline__popover');
  }

  #isVisible() {
    return this.offsetParent !== null;
  }

  #isTypingContext() {
    const el = document.activeElement;
    if (!el) return false;
    return (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement ||
      el.isContentEditable
    );
  }

  open() {
    if (this.hasAttribute('data-open')) return;
    this.setAttribute('data-open', '');
    this.#input?.setAttribute('aria-expanded', 'true');
    this.#popover?.setAttribute('aria-hidden', 'false');
  }

  close() {
    if (!this.hasAttribute('data-open')) return;
    this.removeAttribute('data-open');
    this.#input?.setAttribute('aria-expanded', 'false');
    this.#popover?.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('search-inline')) {
  customElements.define('search-inline', SearchInline);
}
